import { FormEvent, useEffect, useRef, useState } from 'react';
import { ArrowRight, CheckCircle2, Mail, X } from 'lucide-react';
import { subscribeToNewsletter } from '../lib/api';

export function NewsletterModal({
  clientId,
  open,
  onClose,
  onSubscribed
}: {
  clientId: string;
  open: boolean;
  onClose: () => void;
  onSubscribed: () => void;
}) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(5);
  const inputRef = useRef<HTMLInputElement>(null);
  const onCloseRef = useRef(onClose);
  const onSubscribedRef = useRef(onSubscribed);
  onCloseRef.current = onClose;
  onSubscribedRef.current = onSubscribed;

  useEffect(() => {
    if (!open) return;
    setError('');
    setSubscribed(false);
    setSecondsLeft(5);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  useEffect(() => {
    if (!open || !subscribed) return;

    const interval = window.setInterval(() => {
      setSecondsLeft((current) => Math.max(1, current - 1));
    }, 1000);
    const timeout = window.setTimeout(() => onSubscribedRef.current(), 5000);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, [open, subscribed]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !submitting && !subscribed) onCloseRef.current();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, subscribed, submitting]);

  if (!open) return null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await subscribeToNewsletter(email, clientId);
      setSubscribed(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not complete signup. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="newsletter-overlay"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && !submitting && !subscribed && onClose()}
    >
      <section className="newsletter-modal" role="dialog" aria-modal="true" aria-labelledby="newsletter-modal-title">
        {subscribed ? (
          <div className="newsletter-success" aria-live="polite">
            <span className="newsletter-success-icon" aria-hidden="true">
              <CheckCircle2 size={30} />
            </span>
            <p className="newsletter-kicker">You're subscribed</p>
            <h2 id="newsletter-modal-title">Your final preview is unlocked</h2>
            <p className="newsletter-copy">
              Thank you for joining us. Your new portrait will begin in {secondsLeft} {secondsLeft === 1 ? 'second' : 'seconds'}.
            </p>
            <div className="newsletter-success-progress" aria-hidden="true">
              <span />
            </div>
          </div>
        ) : (
          <>
            <button className="newsletter-close" type="button" onClick={onClose} disabled={submitting} aria-label="Close newsletter signup">
              <X size={20} />
            </button>
            <span className="newsletter-icon" aria-hidden="true">
              <Mail size={25} />
            </span>
            <p className="newsletter-kicker">One final preview</p>
            <h2 id="newsletter-modal-title">Unlock your final free portrait</h2>
            <p className="newsletter-copy">Join our email list to use your last free preview for today and receive portrait inspiration and special offers.</p>
            <form onSubmit={submit}>
              <label htmlFor="newsletter-email">Email address</label>
              <div className="newsletter-input-row">
                <input
                  ref={inputRef}
                  id="newsletter-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  maxLength={254}
                  placeholder="you@example.com"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.currentTarget.value)}
                />
                <button type="submit" disabled={submitting}>
                  <span>{submitting ? 'Subscribing...' : 'Subscribe & Continue'}</span>
                  {!submitting && <ArrowRight size={18} />}
                </button>
              </div>
              {error && <p className="newsletter-error" role="alert">{error}</p>}
              <p className="newsletter-consent">
                By subscribing, you agree to receive marketing emails. You can unsubscribe at any time. See our{' '}
                <a href="/pages/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>.
              </p>
            </form>
          </>
        )}
      </section>
    </div>
  );
}
